import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ContractApproval {
  id: string;
  contract_id: string;
  tenant_id: string;
  approval_level: number;
  approver_user_id: string;
  status: "pending" | "approved" | "rejected";
  comments: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  created_at: string;
  updated_at: string;
  profiles?: { first_name: string | null; last_name: string | null; email: string } | null;
}

export function useContractApprovals(contractId: string) {
  return useQuery({
    queryKey: ["contract-approvals", contractId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_approvals")
        .select(`
          *,
          profiles:approver_user_id(first_name, last_name, email)
        `)
        .eq("contract_id", contractId)
        .order("approval_level", { ascending: true });

      if (error) throw error;
      return data as unknown as ContractApproval[];
    },
    enabled: !!contractId,
  });
}

export function useMyPendingApprovals() {
  return useQuery({
    queryKey: ["my-pending-approvals"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("contract_approvals")
        .select(`
          *,
          contracts(id, title, status, signer_name, signer_email, deals(title, amount))
        `)
        .eq("approver_user_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useCreateApprovalChain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      contractId, 
      approvers 
    }: { 
      contractId: string; 
      approvers: { userId: string; level: number }[] 
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) throw new Error("No tenant assigned");

      // Create approval entries
      const approvalEntries = approvers.map(a => ({
        contract_id: contractId,
        tenant_id: profile.tenant_id,
        approver_user_id: a.userId,
        approval_level: a.level,
        status: "pending" as const,
      }));

      const { error: approvalError } = await supabase
        .from("contract_approvals")
        .insert(approvalEntries);

      if (approvalError) throw approvalError;

      // Update contract to require approval
      const { error: contractError } = await supabase
        .from("contracts")
        .update({ 
          approval_required: true, 
          approval_status: "pending" 
        })
        .eq("id", contractId);

      if (contractError) throw contractError;
    },
    onSuccess: (_, { contractId }) => {
      queryClient.invalidateQueries({ queryKey: ["contract-approvals", contractId] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["my-pending-approvals"] });
      toast.success("Approval workflow created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useApproveContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      approvalId, 
      contractId,
      comments 
    }: { 
      approvalId: string; 
      contractId: string;
      comments?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Update this approval
      const { error: approvalError } = await supabase
        .from("contract_approvals")
        .update({
          status: "approved",
          approved_at: new Date().toISOString(),
          comments,
        })
        .eq("id", approvalId);

      if (approvalError) throw approvalError;

      // Check if all approvals are done
      const { data: pendingApprovals } = await supabase
        .from("contract_approvals")
        .select("id")
        .eq("contract_id", contractId)
        .eq("status", "pending");

      if (!pendingApprovals || pendingApprovals.length === 0) {
        // All approved - update contract
        await supabase
          .from("contracts")
          .update({ approval_status: "approved" })
          .eq("id", contractId);
      }

      return { allApproved: !pendingApprovals || pendingApprovals.length === 0 };
    },
    onSuccess: (result, { contractId }) => {
      queryClient.invalidateQueries({ queryKey: ["contract-approvals", contractId] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["my-pending-approvals"] });
      toast.success(result.allApproved 
        ? "Contract fully approved and ready to send!" 
        : "Approval recorded"
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useRejectContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      approvalId, 
      contractId,
      comments 
    }: { 
      approvalId: string; 
      contractId: string;
      comments: string;
    }) => {
      // Update this approval
      const { error: approvalError } = await supabase
        .from("contract_approvals")
        .update({
          status: "rejected",
          rejected_at: new Date().toISOString(),
          comments,
        })
        .eq("id", approvalId);

      if (approvalError) throw approvalError;

      // Update contract status
      await supabase
        .from("contracts")
        .update({ approval_status: "rejected" })
        .eq("id", contractId);
    },
    onSuccess: (_, { contractId }) => {
      queryClient.invalidateQueries({ queryKey: ["contract-approvals", contractId] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["my-pending-approvals"] });
      toast.success("Contract rejected");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
